
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import ConsultModal from '../../components/feature/ConsultModal';
import ScrollToTop from '../../components/feature/ScrollToTop';
import SeoHead from '../../components/feature/SeoHead';
import { getLangPath, type Language } from '../../router/langRoutes';
import HeroSection from './components/HeroSection';
import IntroSection from './components/IntroSection';
import ProductCategoriesSection from './components/ProductCategoriesSection';
import KioskTypesSection from './components/KioskTypesSection';
import AddOnServicesSection from './components/AddOnServicesSection';
import SupportSection from './components/SupportSection';
import GallerySection from './components/GallerySection';
import RegisterFormSection from './components/RegisterFormSection';
import CTABannerSection from './components/CTABannerSection';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://thedaeg.com';

const seoData: Record<string, { title: string; description: string; keywords: string }> = {
  vi: {
    title: 'Chợ Hẻm Asian Alley – Thuê Kiosk Kinh Doanh Hàng Châu Á tại Riga Latvia | The Daeg',
    description:
      'Thuê Kiosk kinh doanh hàng Châu Á tại Chợ Hẻm Asian Alley Mini Mall – Riga, Latvia. Kích thước 1.7×2.5m đến 2.5×2.5m, giá chỉ từ 10 EUR/ngày hoặc 150 EUR/tháng. Hỗ trợ giấy tờ, ngôn ngữ, vận hành toàn diện.',
    keywords:
      'chợ hẻm asian alley, thuê kiosk riga latvia, kinh doanh hàng châu á châu âu, kiosk mini mall latvia, asian alley mini mall',
  },
  en: {
    title: 'Cho Hem Asian Alley – Rent Kiosk for Asian Business in Riga Latvia | The Daeg',
    description:
      'Rent a kiosk for Asian goods business at Cho Hem Asian Alley Mini Mall – Riga, Latvia. Size 1.7×2.5m to 2.5×2.5m, from only 10 EUR/day or 150 EUR/month. Full support for paperwork, language, and operations.',
    keywords:
      'Asian Alley kiosk rental, Riga Latvia kiosk, Asian business Europe, mini mall Latvia, cho hem Asian Alley',
  },
  lv: {
    title: 'Cho Hem Asian Alley – Kioski Noma Āzijas Biznesam Rīgā Latvijā | The Daeg',
    description:
      'Nomājiet kioski Āzijas preču tirdzniecībai Cho Hem Asian Alley Mini Mall – Rīgā, Latvijā. Izmērs 1.7×2.5m līdz 2.5×2.5m, no 10 EUR/dienā vai 150 EUR/mēnesī.',
    keywords:
      'Asian Alley kioski noma, Rīga Latvija kioski, Āzijas bizness Eiropa, mini mall Latvija',
  },
};

const ChoHemPage = () => {
  const { i18n } = useTranslation('choHem');
  const [isConsultOpen, setIsConsultOpen] = useState(false);
  const registerRef = useRef<HTMLDivElement>(null);

  const lang = (i18n.language in seoData ? i18n.language : 'vi') as Language;
  const seo = seoData[lang];

  const kioskSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'Chợ Hẻm Asian Alley Mini Mall',
    description: seo.description,
    url: `${SITE_URL}${getLangPath('choHem', lang)}`,
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Aspazijas Bulvaris 32',
      addressLocality: 'Riga',
      addressCountry: 'LV',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 56.9496,
      longitude: 24.1052,
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
        opens: '11:00',
        closes: '19:00',
      },
    ],
    telephone: '+371-2572-6969',
    priceRange: '€€',
    offers: {
      '@type': 'Offer',
      name: 'Thuê Kiosk Kinh Doanh',
      price: '10',
      priceCurrency: 'EUR',
      description: 'Thuê kiosk kinh doanh hàng Châu Á từ 10 EUR/ngày hoặc 150 EUR/tháng',
    },
  };

  const scrollToRegister = () => {
    registerRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-black">
      <SeoHead
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonicalPath={getLangPath('choHem', lang)}
        schemaJson={kioskSchema}
      />
      <header>
        <Navbar onConsultClick={() => setIsConsultOpen(true)} />
      </header>

      <main>
        <HeroSection onRegisterClick={scrollToRegister} />
        <IntroSection />
        <ProductCategoriesSection />
        <KioskTypesSection onRegisterClick={scrollToRegister} />
        <AddOnServicesSection onRegisterClick={scrollToRegister} />
        <SupportSection />
        <GallerySection />
        <CTABannerSection onRegisterClick={scrollToRegister} />
        <div ref={registerRef}>
          <RegisterFormSection />
        </div>
      </main>

      <Footer />
      <ConsultModal isOpen={isConsultOpen} onClose={() => setIsConsultOpen(false)} />
      <ScrollToTop />
    </div>
  );
};

export default ChoHemPage;
